/**
 * Brush clearance — would the arrow's trajectory clear an obstacle at a
 * given distance and height?
 *
 * The trajectory result includes `apexHeightIn` and `apexRangeYards`. For
 * clearance we want to know: at the distance the brush sits at, how high
 * is the arrow above the sight line?
 *
 * The math: the trajectory's height ABOVE the sight line at any point
 * depends on the launch angle the bow is held at to reach the target. We
 * approximate by looking at the apex height as the worst-case clearance
 * needed, and providing a per-distance interpolation.
 *
 * For v140.0 this is a simple "does the apex height exceed the brush
 * height at any point before the brush distance" check. v140.1 could
 * do per-yard height interpolation for finer detail.
 */

import type { TrajectoryResult, Environment } from './types';

export interface BrushClearanceResult {
  enabled: boolean;
  clears: boolean;
  arrowHeightAtBrushIn: number;
  brushHeightIn: number;
  brushAtYards: number;
  clearanceMarginIn: number;
}

/**
 * Returns clearance info if brush parameters are set in env.
 */
export function computeBrushClearance(
  traj: TrajectoryResult,
  env: Environment
): BrushClearanceResult {
  if (env.brushHeightIn == null || env.brushAtYards == null) {
    return {
      enabled: false,
      clears: true,
      arrowHeightAtBrushIn: 0,
      brushHeightIn: 0,
      brushAtYards: 0,
      clearanceMarginIn: 0,
    };
  }

  /* Approximate arrow height at the brush distance.
   *
   * For a trajectory with apex height H at distance A out of a total range
   * R, the height above sight line at distance d (where 0 ≤ d ≤ R) follows
   * a parabolic-ish curve peaked at A. We approximate as:
   *
   *   h(d) ≈ H × (1 - ((d - A) / max(A, R - A))²)
   *
   * for d ≤ R. Beyond R, the arrow is below the sight line.
   */
  const apex = traj.apexHeightIn;
  const apexAt = traj.apexRangeYards;
  const totalRange = traj.apexRangeYards * 2; /* approximate full flight at sight line */
  const d = env.brushAtYards;
  const lobe = Math.max(apexAt, totalRange - apexAt);
  const normalizedOffset = (d - apexAt) / lobe;
  const arrowHeightAtBrushIn = Math.max(0, apex * (1 - normalizedOffset * normalizedOffset));

  const margin = arrowHeightAtBrushIn - env.brushHeightIn;
  return {
    enabled: true,
    clears: margin >= 0,
    arrowHeightAtBrushIn,
    brushHeightIn: env.brushHeightIn,
    brushAtYards: env.brushAtYards,
    clearanceMarginIn: margin,
  };
}
